import { useMemo, useState } from 'react'

/** Элемент прогона в том виде, в каком его отдаёт служба. */
export interface ItemView {
  json: Record<string, unknown>
}

export interface DataPanelProps {
  items: readonly ItemView[]
  /** Заголовок панели: обычно имя ноды, чьи данные показаны. */
  title?: string
  /**
   * Образец из описания узла: показывается вместо пустоты, пока прогона не
   * было. Отдельным свойством, а не подмешанным в `items`, именно потому,
   * что панель обязана сказать, что это образец: принять выдуманные данные
   * за настоящие — значит уйти отлаживать несуществующую беду.
   */
  sample?: readonly ItemView[]
}

/**
 * Тип переноса. Панель данных и панель параметров общаются только через
 * него: панели не знают друг о друге, и вставить поле можно в любое
 * текстовое поле формы, а не в заранее перечисленные.
 */
export const FIELD_DRAG_TYPE = 'text/plain'

/**
 * Обращение к сегменту пути. Простое имя пишется через точку, всё
 * остальное — скобками со строкой: `$json.заказ-1` разобралось бы как
 * вычитание, а `$json.2` не разобралось бы вовсе.
 */
function segmentAccess(segment: string): string {
  return /^[\p{L}_][\p{L}\p{N}_]*$/u.test(segment) ? `.${segment}` : `[${JSON.stringify(segment)}]`
}

/** Выражение, которое достаёт поле по пути: `from` → `{{ $json.from }}`. */
export function fieldExpression(path: readonly string[]): string {
  return `{{ $json${path.map(segmentAccess).join('')} }}`
}

/** Раскрываются только простые объекты: у массива путь к элементу — индекс,
 * а индексы в выражениях появятся вместе с нодой перебора. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function preview(value: unknown): string {
  if (typeof value === 'string') return value
  return JSON.stringify(value) ?? 'undefined'
}

interface FieldProps {
  path: readonly string[]
  children: React.ReactNode
}

/** Перетаскиваемое имя поля. Одно и то же и в дереве, и в шапке таблицы. */
function DraggableField({ path, children }: FieldProps): React.JSX.Element {
  return (
    <span
      className="поле-данных"
      draggable
      title={fieldExpression(path)}
      onDragStart={(event) => {
        event.dataTransfer.setData(FIELD_DRAG_TYPE, fieldExpression(path))
        event.dataTransfer.effectAllowed = 'copy'
      }}
    >
      {children}
    </span>
  )
}

function FieldTree({
  json,
  prefix,
}: {
  json: Record<string, unknown>
  prefix: readonly string[]
}): React.JSX.Element {
  return (
    <ul className="дерево-данных">
      {Object.entries(json).map(([key, value]) => {
        const path = [...prefix, key]
        return (
          <li key={key}>
            <DraggableField path={path}>{key}</DraggableField>
            {isPlainObject(value) ? (
              <FieldTree json={value} prefix={path} />
            ) : (
              <span className="поле-данных__значение">{preview(value)}</span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Столбцы таблицы: все верхние поля в порядке первой встречи. */
function columnsOf(items: readonly ItemView[]): string[] {
  const seen = new Set<string>()
  for (const item of items) {
    for (const key of Object.keys(item.json)) seen.add(key)
  }
  return [...seen]
}

/**
 * Панель данных: что пришло на вход ноды и что она отдала. Поля из неё
 * переносятся в параметры — набирать `{{ $json.from }}` руками значит
 * ошибаться в имени поля, которое видно на экране рядом.
 *
 * Два вида: дерево полей и таблица. Дерево показывает вложенность и
 * значения одного элемента, таблица — один и тот же набор полей у сотни
 * элементов; ни один из видов не заменяет другой.
 */
export function DataPanel({ items, title, sample = [] }: DataPanelProps): React.JSX.Element {
  const [view, setView] = useState<'tree' | 'table'>('tree')
  // Данные прогона всегда важнее образца: образец нужен ровно до тех пор,
  // пока настоящих данных нет.
  const образец = items.length === 0 && sample.length > 0
  const показать = образец ? sample : items
  const columns = useMemo(() => columnsOf(показать), [показать])

  return (
    <section
      className={`панель-данных${образец ? ' панель-данных--образец' : ''}`}
      aria-label={title ?? 'Данные'}
    >
      <header>
        {title !== undefined ? <h3>{title}</h3> : null}
        <p className="панель-данных__счётчик">{`Элементов: ${показать.length}`}</p>
        <div role="group" aria-label="Вид данных">
          <button
            type="button"
            aria-pressed={view === 'tree'}
            onClick={() => {
              setView('tree')
            }}
          >
            JSON
          </button>
          <button
            type="button"
            aria-pressed={view === 'table'}
            onClick={() => {
              setView('table')
            }}
          >
            Таблица
          </button>
        </div>
      </header>

      {/* Образец назван образцом до самих данных, а не после: увидев поля,
          человек начинает их читать, и предупреждение под ними опоздает. */}
      {образец ? (
        <p className="панель-данных__образец">
          Это образец из описания узла, а не результат прогона: полей больше не станет, а
          значения выдуманы. Запустите тестовый прогон — здесь появится то, что пришло на
          самом деле.
        </p>
      ) : null}

      {показать.length === 0 ? (
        <p className="панель-данных__пусто">
          Данных пока нет. Запустите тестовый прогон — сюда попадёт то, что пришло на вход
          этого узла, и поля можно будет перетащить прямо в параметры.
        </p>
      ) : view === 'tree' ? (
        показать.map((item, index) => (
          // Индекс — единственное, чем элементы различаются: своего
          // идентификатора у элемента прогона нет.
          <div key={index}>
            <h4>{`Элемент ${index + 1}`}</h4>
            <FieldTree json={item.json} prefix={[]} />
          </div>
        ))
      ) : (
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column} scope="col">
                  <DraggableField path={[column]}>{column}</DraggableField>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {показать.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column}>
                    {Object.prototype.hasOwnProperty.call(item.json, column)
                      ? preview(item.json[column])
                      : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
